import React, { useState, useEffect } from 'react';
import RowActions from '../../../components/admin/RowActions';
import StatusChip from '../../../components/admin/StatusChip';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Chip,
  Skeleton,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  SportsEsports as SportsEsportsIcon,
  ToggleOn,
  ToggleOff,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../../api';
import { toast } from 'react-toastify';

const DIFFICULTIES = [
  { value: 'easy', label: 'Easy', color: '#1f9d55', bg: '#e6f7ec' },
  { value: 'normal', label: 'Medium', color: '#c9821a', bg: '#fff4e0' },
  { value: 'hard', label: 'Hard', color: '#d64545', bg: '#fde8e8' },
];

const difficultyMeta = (value) => {
  const key = String(value || '').toLowerCase() === 'medium' ? 'normal' : String(value || '').toLowerCase();
  return DIFFICULTIES.find((d) => d.value === key) || null;
};

const GameContentList = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [gameTypeFilter, setGameTypeFilter] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [gameTypes, setGameTypes] = useState([]);
  const [subjects, setSubjects] = useState([]);

  useEffect(() => {
    api.get('admin/game-contents/game-types').then((r) => {
      if (r.data?.success && r.data?.data) setGameTypes(r.data.data);
    }).catch(() => {});
    api.get('admin/game-contents/subjects').then((r) => {
      if (r.data?.success && r.data?.data) setSubjects(r.data.data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    fetchList();
  }, [page, rowsPerPage, gameTypeFilter, subjectFilter, statusFilter, difficultyFilter]);

  const fetchList = async () => {
    setLoading(true);
    try {
      const params = { page: page + 1, per_page: rowsPerPage };
      if (gameTypeFilter) params.game_type = gameTypeFilter;
      if (subjectFilter) params.subject_id = subjectFilter;
      if (statusFilter !== '') params.status = statusFilter;
      if (difficultyFilter) params.difficulty = difficultyFilter;
      const res = await api.get('admin/game-contents', { params });
      if (res.data?.success && res.data?.data) {
        const pag = res.data.data;
        setItems(Array.isArray(pag.data) ? pag.data : []);
        setTotalRecords(pag.total ?? 0);
      } else {
        setItems([]);
        setTotalRecords(0);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load game contents');
      setItems([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (row) => {
    setSelectedItem(row);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedItem) return;
    try {
      await api.delete(`admin/game-contents/${selectedItem.id}`);
      toast.success('Game content deleted');
      setDeleteDialogOpen(false);
      setSelectedItem(null);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to delete');
    }
  };

  const handleStatusToggle = async (row) => {
    const newStatus = row.status === 2 ? 1 : 2;
    try {
      await api.patch(`admin/game-contents/${row.id}/status`, { status: newStatus });
      toast.success('Status updated');
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to update status');
    }
  };

  const getStatusLabel = (status) => (status === 2 ? 'Active' : 'Inactive');
  const getStatusColor = (status) => (status === 2 ? 'success' : 'default');

  const contentPreview = (content) => {
    if (!content) return '—';
    if (typeof content === 'string') return content.slice(0, 50);
    const q = content.question || content.title || '';
    if (q) return String(q).slice(0, 50) + (String(q).length > 50 ? '…' : '');
    return JSON.stringify(content).slice(0, 50) + '…';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography component="h1" sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 700, color: '#111827', lineHeight: 1.3, mb: 2 }}>
          Game Contents
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 1 }}>
          <Typography variant="body1" sx={{ color: '#5a6c7d' }}>
            Manage questions and content items for each game type.
          </Typography>
          {!loading && (
            <Chip label={`Total: ${totalRecords}`} size="small" sx={{ bgcolor: '#5e35b1', color: 'white' }} />
          )}
        </Box>
      </Box>

      <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Game type</InputLabel>
          <Select
            value={gameTypeFilter}
            onChange={(e) => { setGameTypeFilter(e.target.value); setPage(0); }}
            label="Game type"
          >
            <MenuItem value="">All</MenuItem>
            {gameTypes.map((key) => (
              <MenuItem key={key} value={key}>{key}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Subject</InputLabel>
          <Select
            value={subjectFilter}
            onChange={(e) => { setSubjectFilter(e.target.value); setPage(0); }}
            label="Subject"
          >
            <MenuItem value="">All</MenuItem>
            {subjects.map((s) => (
              <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            label="Status"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value={1}>Inactive</MenuItem>
            <MenuItem value={2}>Active</MenuItem>
          </Select>
        </FormControl>
        {DIFFICULTIES.map((lvl) => {
          const active = difficultyFilter === lvl.value;
          return (
            <Chip
              key={lvl.value}
              label={lvl.label}
              size="small"
              clickable
              onClick={() => {
                setDifficultyFilter(active ? '' : lvl.value);
                setPage(0);
              }}
              sx={{
                fontWeight: 600,
                bgcolor: active ? lvl.bg : 'transparent',
                color: active ? lvl.color : 'text.secondary',
                border: '1px solid',
                borderColor: active ? lvl.color : 'divider',
              }}
            />
          );
        })}
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/admin/game-contents/add')}
          sx={{ bgcolor: '#5e35b1', '&:hover': { bgcolor: '#4527a0' } }}
        >
          Add Game Content
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600, width: '12%' }}>Game type</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '12%' }}>Subject</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '14%' }}>Topic</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '14%' }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '20%' }}>Content preview</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '8%' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Difficulty</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 120 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                    <TableCell><Skeleton variant="text" width={80} /></TableCell>
                    <TableCell><Skeleton variant="text" width={100} /></TableCell>
                    <TableCell><Skeleton variant="text" width={120} /></TableCell>
                    <TableCell><Skeleton variant="text" width={150} /></TableCell>
                    <TableCell><Skeleton variant="text" width={60} /></TableCell>
                    <TableCell><Skeleton variant="text" width={50} /></TableCell>
                    <TableCell><Skeleton variant="circular" width={32} height={32} /></TableCell>
                  </TableRow>
                ))
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <SportsEsportsIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                    <Typography color="text.secondary">No game contents found</Typography>
                    <Button startIcon={<AddIcon />} onClick={() => navigate('/admin/game-contents/add')} sx={{ mt: 2 }}>
                      Add Game Content
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell><Tooltip title={row.game_type || ''}><span><StatusChip tone="grey" status={row.game_type} sx={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'middle' }} /></span></Tooltip></TableCell>
                    <TableCell>{row.subject_name || '—'}</TableCell>
                    <TableCell>{row.topic_name || '—'}</TableCell>
                    <TableCell>{row.title || '—'}</TableCell>
                    <TableCell
                      sx={{
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        whiteSpace: 'normal',
                      }}
                    >
                      {contentPreview(row.content)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(row.status)}
                        color={getStatusColor(row.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {difficultyMeta(row.difficulty) ? (
                        <Chip
                          label={difficultyMeta(row.difficulty).label}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            bgcolor: difficultyMeta(row.difficulty).bg,
                            color: difficultyMeta(row.difficulty).color,
                          }}
                        />
                      ) : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <RowActions
                        actions={[
                          { label: 'View', icon: <ViewIcon fontSize="small" />, onClick: () => navigate(`/admin/game-contents/${row.id}`) },
                          { label: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => navigate(`/admin/game-contents/${row.id}/edit`) },
                          { label: row.status === 2 ? 'Set inactive' : 'Set active', icon: row.status === 2 ? <ToggleOff /> : <ToggleOn />, onClick: () => handleStatusToggle(row) },
                          { label: 'Delete', icon: <DeleteIcon />, onClick: () => handleDeleteClick(row), danger: true },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!loading && items.length > 0 && (
          <TablePagination
            component="div"
            count={totalRecords}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        )}
      </Paper>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Game Content</DialogTitle>
        <DialogContent>
          <Typography>
            Delete this game content? (Game type: {selectedItem?.game_type}, Title: {selectedItem?.title || '—'})
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GameContentList;
